import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

interface FeedbackContextType {
	isWidgetOpen: boolean;
	openWidget: () => void;
	closeWidget: () => void;
	getConsoleErrors: () => string[];
}

const FeedbackContext = createContext<FeedbackContextType | null>(null);

export const useFeedbackContext = () => {
	const ctx = useContext(FeedbackContext);
	if (!ctx) throw new Error('useFeedbackContext must be used within FeedbackProvider');
	return ctx;
};

const JWT_PATTERN = /eyJ[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}/g;
const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const SECRET_PATTERN = /\b[a-zA-Z0-9+/]{32,}\b/g;

const sanitizeError = (message: string): string =>
	message
		.replace(JWT_PATTERN, '[JWT_REDACTED]')
		.replace(EMAIL_PATTERN, '[EMAIL_REDACTED]')
		.replace(SECRET_PATTERN, '[SECRET_REDACTED]');

export const FeedbackProvider = ({ children }: { children: ReactNode }) => {
	const [isWidgetOpen, setIsWidgetOpen] = useState(false);
	const errorBufferRef = useRef<string[]>([]);

	// Listen to real runtime errors via the global error events instead of
	// monkey-patching `console.error`. The previous implementation replaced
	// `console.error` with a wrapper, which had two costs:
	//   1. React's "Maximum update depth exceeded" (and every other React
	//      warning) had its component-stack attribution clobbered — the
	//      error appeared to originate from the wrapper's source position
	//      (FeedbackProvider.tsx:40) instead of the offending component,
	//      making infinite-render bugs effectively undebuggable.
	//   2. Any other library that wanted to patch `console.error` (test
	//      runners, Sentry, etc.) would conflict.
	//
	// The new approach captures uncaught errors and unhandled promise
	// rejections — the genuinely diagnostic surface — without touching
	// React's warning channel.
	useEffect(() => {
		const handleError = (event: ErrorEvent) => {
			const msg = sanitizeError(event.message || String(event.error));
			errorBufferRef.current = [...errorBufferRef.current.slice(-9), msg];
		};
		const handleRejection = (event: PromiseRejectionEvent) => {
			const msg = sanitizeError(`Unhandled rejection: ${String(event.reason)}`);
			errorBufferRef.current = [...errorBufferRef.current.slice(-9), msg];
		};
		window.addEventListener('error', handleError);
		window.addEventListener('unhandledrejection', handleRejection);
		return () => {
			window.removeEventListener('error', handleError);
			window.removeEventListener('unhandledrejection', handleRejection);
		};
	}, []);

	const getConsoleErrors = useCallback(() => errorBufferRef.current, []);
	const openWidget = useCallback(() => setIsWidgetOpen(true), []);
	const closeWidget = useCallback(() => setIsWidgetOpen(false), []);

	// Memoize the context value. Without this, the value object is a new
	// reference every render — every consumer of `useFeedbackContext()`
	// re-renders even when `isWidgetOpen` hasn't changed, which combined
	// with their own state-tracking effects can amplify a single state
	// change into a render storm.
	const value = useMemo<FeedbackContextType>(
		() => ({ isWidgetOpen, openWidget, closeWidget, getConsoleErrors }),
		[isWidgetOpen, openWidget, closeWidget, getConsoleErrors]
	);

	return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
};
